import dayjs from 'dayjs'
import { orderBy, sortBy } from 'lodash-es'

import { InfuraUsage } from 'components/billing/usage/usage-types'

import { InfuraStats } from 'lib/stats/stats-types'

export const GROUPING_THRESHOLD = 6

const formatToChartDate = (timeString: string) => dayjs(timeString).utc().format('YYYY-MM-DD:HH:mm')

type DailyCreditUsageSerializer = {
  isUsageEnabled: boolean
  groupingName?: string
  rawData?: InfuraStats.MethodVolumeRawData
  quota: number
}

export const serializer = ({
  groupingName = 'All Other',
  isUsageEnabled,
  quota,
  rawData
}: DailyCreditUsageSerializer): InfuraUsage.TimeseriesData => {
  const labels = [] as Array<InfuraUsage.ChartLabelConfig>
  const groupedItems = [] as Array<string>
  const timeseries: InfuraUsage.LineChartTimeseries = []

  if (!rawData) {
    return {
      groupedItems,
      labels,
      timeseries
    }
  }

  // Sort all of the data points returned by the API.
  const sorted = sortBy(rawData, item => item.ts).map(item => {
    const mappedName = item.method
    const labelExists = labels.some(label => label.text === mappedName)
    if (!labelExists) {
      const label: InfuraUsage.ChartLabelConfig = {
        dataKey: '',
        text: mappedName,
        tooltipText: isUsageEnabled ? item.total : item.value
      }
      labels.push(label)
    } else {
      const updateItemIndex = labels.findIndex(item1 => item1.text === mappedName)
      const previousTotal = +labels[updateItemIndex].tooltipText
      labels[updateItemIndex].tooltipText = (isUsageEnabled ? item.total : item.value) + previousTotal
    }

    return {
      ...item,
      date: formatToChartDate(item.ts)
    }
  })

  // Update and group the labels if there are more than the threshold
  const sortedAndGroupedLabels = orderBy(labels, item => item.tooltipText, 'desc')

  if (sortedAndGroupedLabels.length > GROUPING_THRESHOLD) {
    sortedAndGroupedLabels.slice(GROUPING_THRESHOLD - 1).map(item => {
      groupedItems.push(item.text)
    })

    sortedAndGroupedLabels.push({
      dataKey: '',
      text: groupingName,
      tooltipText: 0
    })

    sortedAndGroupedLabels.forEach(item => {
      if (groupedItems.includes(item.text)) {
        const lastItem = sortedAndGroupedLabels.at(-1) as InfuraUsage.ChartLabelConfig
        const newTotal = Number(lastItem.tooltipText) + Number(item.tooltipText)
        lastItem.tooltipText = newTotal
      }
    })
  }

  const allItems = sorted.reduce(
    (acc, item) => {
      const methodName = item.method
      if (groupedItems.includes(methodName)) {
        acc[groupingName] = (acc[groupingName] ?? 0) + (isUsageEnabled ? item.total : item.value)
      }
      if (acc[methodName]) {
        acc[methodName] += isUsageEnabled ? item.total : item.value
      } else {
        acc[methodName] = isUsageEnabled ? item.total : item.value
      }
      return acc
    },
    {
      date: 'dummy',
      eightyFivePercent: quota * 0.85,
      ninetyPercent: quota * 0.9,
      seventyFivePercent: quota * 0.75
    } as InfuraUsage.TimeseriesDataPoint & {
      seventyFivePercent: number
      eightyFivePercent: number
      ninetyPercent: number
    }
  )

  // Temporarily removing the all other group
  delete allItems[groupingName]
  const filteredLabels = sortedAndGroupedLabels.filter(label => label.text !== groupingName)

  timeseries.push(allItems)

  return {
    groupedItems,
    labels: filteredLabels,
    timeseries
  }
}
